import { useTranslation } from '@hbs-us/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ApiError } from '../../lib/apiFetch.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { AssignmentEditDialog, type EditPick } from './AssignmentEditDialog.js';
import type { DispatchAssignmentDto, DispatchJobDto } from './contractsMirror.js';
import { DispatchBoard } from './DispatchBoard.js';
import { DispatchDayView } from './DispatchDayView.js';
import { DispatchManageView } from './DispatchManageView.js';
import { ScheduleFallbackDialog, type SchedulePick } from './ScheduleFallbackDialog.js';
import {
  useAssignMutation,
  useEditMutation,
  useRescheduleMutation,
  useUnassignMutation,
} from './useAssignMutation.js';
import { useDispatchWeekQuery } from './useDispatchWeekQuery.js';
import { nextWeek, prevWeek } from './weekModel.js';

/**
 * The `/dispatch` route component (a NEW route — never a ComingSoon stub). Reads the typed `week`
 * search param, fires the coarse-endpoint TanStack Query, and renders the accessible week board on
 * success (the reports `ReportsRoute` 3-state contract: loading → a page-shaped `RouteSkeleton`; error → announced
 * retry; success → the board).
 *
 * The server resolves the Monday-start week + aggregates the assignments / jobs / categories / crew;
 * this component never re-aggregates and never sends a tenant id (SEC-11). The week prev / next /
 * today nav recomputes the `week=` value via `weekModel` (mirroring the legacy Monday-start math)
 * and navigates — which drives the coarse refetch.
 *
 * A11Y-01: the `DispatchBoard` wires both sensors + announcements; EVERY job card also opens the
 * `ScheduleFallbackDialog` here (the non-drag keyboard path), which hits the SAME assign mutation.
 */

const routeApi = getRouteApi('/dispatch');

export function DispatchRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = routeApi.useSearch();
  const query = useDispatchWeekQuery(search);

  const assignMutation = useAssignMutation();
  const rescheduleMutation = useRescheduleMutation();
  const editMutation = useEditMutation();
  const unassignMutation = useUnassignMutation();

  // The job whose "Schedule…" fallback dialog is open (the non-drag keyboard path).
  const [scheduleJob, setScheduleJob] = useState<DispatchJobDto | null>(null);
  // The day and time a job was dropped on, so the Schedule dialog opens there.
  const [scheduleSlot, setScheduleSlot] = useState<{ date: string; startTime: string } | null>(
    null,
  );
  // Why the last write failed. Every dispatch write used to fail silently: the block snapped back
  // on the next poll and the dispatcher believed the change had been made.
  const [writeError, setWriteError] = useState<string | null>(null);
  // The assignment being edited (full-field dialog) / removed (destructive confirm).
  const [editAssignment, setEditAssignment] = useState<DispatchAssignmentDto | null>(null);
  const [unassignTarget, setUnassignTarget] = useState<DispatchAssignmentDto | null>(null);
  // The selected day for the small-screen agenda view (defaults to today / week start).
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  /**
   * Crew members the last action could not email, by name.
   *
   * The dispatch mailer needs an active user account to find an address, and silently sends nothing
   * when there isn't one — so scheduling somebody who has no login reported a clean success while
   * that person was never told. This is the only place that fact surfaces; without it the office
   * believes a tech is expecting the job.
   */
  const [unnotified, setUnnotified] = useState<string[]>([]);

  /** Carry a write's `unnotified` onto the banner. Every dispatch write routes through this. */
  const noteUnnotified = (res: { unnotified?: string[] } | undefined) => {
    setUnnotified(res?.unnotified ?? []);
  };

  const goToWeek = (week: string) => {
    void navigate({ to: '/dispatch', search: { week } });
  };

  const describeWriteError = (error: Error) => {
    const code = error instanceof ApiError ? error.message : '';
    return t(`dispatch:writeError.${code || 'generic'}`, {
      defaultValue: t('dispatch:writeError.generic'),
    });
  };
  const noteWriteError = (error: Error) => setWriteError(describeWriteError(error));

  const openSchedule = (job: DispatchJobDto, slot: { date: string; startTime: string } | null) => {
    setWriteError(null);
    setScheduleSlot(slot);
    setScheduleJob(job);
  };
  const closeSchedule = () => {
    setWriteError(null);
    setScheduleJob(null);
    setScheduleSlot(null);
  };
  const openEdit = (assignment: DispatchAssignmentDto | null) => {
    setWriteError(null);
    setEditAssignment(assignment);
  };

  const currentWeekStart = query.data?.weekStart;
  const effectiveDay = useMemo(() => {
    if (selectedDay && query.data?.days?.includes(selectedDay)) return selectedDay;
    return query.data?.today && query.data.days?.includes(query.data.today)
      ? query.data.today
      : (query.data?.days?.[0] ?? '');
  }, [selectedDay, query.data]);

  if (query.isPending) {
    return (
      <RouteSkeleton
        label={t('dispatch:heading')}
        rows={7}
        columns={3}
        filters={2}
        action={false}
      />
    );
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('dispatch:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('dispatch:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('dispatch:error.retry')}
        </button>
      </section>
    );
  }

  const data = query.data;
  const days = data.days ?? [];
  const jobs = data.jobs ?? [];
  const assignments = data.assignments ?? [];
  const employees = data.employees ?? [];
  const categories = data.categories ?? [];
  const colorPalette = data.categoryColorPalette ?? [];
  const today = data.today ?? '';

  const handleAssignJobToSlot = (jobId: number, date: string, startTime: string) => {
    // A drop says where and when, not who. It used to book whoever sorted first by name, silently,
    // and email them; now it opens the Schedule dialog at that day and time with nobody picked.
    const job = jobs.find((j) => j.id === jobId);
    if (job) openSchedule(job, { date, startTime });
  };

  const handleReschedule = (assignmentId: number, date: string, startTime: string) => {
    // Only the new start is sent; the server keeps the visit's length.
    setWriteError(null);
    rescheduleMutation.mutate(
      { id: assignmentId, scheduledDate: date, startTime },
      { onSuccess: noteUnnotified, onError: noteWriteError },
    );
  };

  const handleFallbackConfirm = (pick: SchedulePick) => {
    if (!scheduleJob) return;
    assignMutation.mutate(
      {
        jobId: scheduleJob.id,
        employeeId: pick.employeeId,
        scheduledDate: pick.scheduledDate,
        startTime: pick.startTime,
        endTime: pick.endTime,
        categoryId: pick.categoryId,
        customerName: pick.customerName,
        customerPhone: pick.customerPhone,
        customerAddress: pick.customerAddress,
      },
      {
        onSuccess: (res) => {
          noteUnnotified(res);
          closeSchedule();
        },
        onError: noteWriteError,
      },
    );
  };

  const handleEditConfirm = (pick: EditPick) => {
    if (!editAssignment) return;
    editMutation.mutate(
      {
        id: editAssignment.id,
        employeeId: pick.employeeId,
        scheduledDate: pick.scheduledDate,
        startTime: pick.startTime,
        endTime: pick.endTime,
        jobId: pick.jobId,
        categoryId: pick.categoryId,
        notes: pick.notes,
        customerName: pick.customerName,
        customerPhone: pick.customerPhone,
        customerAddress: pick.customerAddress,
      },
      {
        onSuccess: (res) => {
          noteUnnotified(res);
          openEdit(null);
        },
        onError: noteWriteError,
      },
    );
  };

  const handleConfirmUnassign = () => {
    if (!unassignTarget) return;
    unassignMutation.mutate(
      { id: unassignTarget.id },
      {
        onSuccess: (res) => {
          noteUnnotified(res);
          setUnassignTarget(null);
        },
        onError: (error) => {
          setUnassignTarget(null);
          noteWriteError(error);
        },
      },
    );
  };

  return (
    <section className="flex h-full flex-col gap-4 bg-background p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('dispatch:heading')}
        </h1>

        {/* Week prev / today / next navigation (Monday-start math via weekModel). */}
        <nav aria-label={t('dispatch:nav.label')} className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => currentWeekStart && goToWeek(prevWeek(currentWeekStart))}
            className="inline-flex h-9 items-center rounded-md bg-card px-3 text-foreground text-sm transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('dispatch:nav.prev')}
          </button>
          <button
            type="button"
            onClick={() => void navigate({ to: '/dispatch', search: {} })}
            className="inline-flex h-9 items-center rounded-md bg-card px-3 text-foreground text-sm transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('dispatch:nav.today')}
          </button>
          <button
            type="button"
            onClick={() => currentWeekStart && goToWeek(nextWeek(currentWeekStart))}
            className="inline-flex h-9 items-center rounded-md bg-card px-3 text-foreground text-sm transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('dispatch:nav.next')}
          </button>
        </nav>
      </header>

      {currentWeekStart ? (
        <p className="text-muted-foreground text-sm">
          {t('dispatch:weekOf', { date: currentWeekStart })}
        </p>
      ) : null}

      {/* The change went through, but somebody was not told about it. Announced rather than shown
          quietly: the board looks identical either way, and the whole point is that the dispatcher
          learns NOW — while they can still pick up the phone — rather than never. */}
      {unnotified.length > 0 ? (
        <div
          role="status"
          aria-live="polite"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          <span className="font-medium text-foreground">
            {t('dispatch:notify.heading', { names: unnotified.join(', ') })}
          </span>
          <span className="text-muted-foreground">{t('dispatch:notify.body')}</span>
          <button
            type="button"
            onClick={() => setUnnotified([])}
            className="ml-auto inline-flex h-7 items-center rounded-sm px-2 font-medium text-[0.7rem] text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('dispatch:notify.dismiss')}
          </button>
        </div>
      ) : null}

      {/* A failed drag or remove has no dialog to show the reason in, so it is announced here. */}
      {writeError && scheduleJob === null && editAssignment === null ? (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
        >
          <span className="font-medium text-foreground">{writeError}</span>
          <button
            type="button"
            onClick={() => setWriteError(null)}
            className="ml-auto inline-flex h-7 items-center rounded-sm px-2 font-medium text-[0.7rem] text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('dispatch:notify.dismiss')}
          </button>
        </div>
      ) : null}

      {/* The drag board (above lg) — wires both sensors + announcements; cards have the fallback. */}
      <div className="hidden lg:block">
        <DispatchBoard
          days={days}
          today={today}
          jobs={jobs}
          assignments={assignments}
          onScheduleJob={(job) => openSchedule(job, null)}
          onAssignJobToSlot={handleAssignJobToSlot}
          onRescheduleToSlot={handleReschedule}
          onEditAssignment={openEdit}
          onUnassignAssignment={setUnassignTarget}
        />
      </div>

      {/* The day / agenda view (below lg) — small screens get an accessible single-day list. The
          Unscheduled rail's Schedule… buttons stay available so assignment is never pointer-only. */}
      <div className="lg:hidden">
        <DispatchDayView
          days={days}
          selectedDate={effectiveDay}
          assignments={assignments}
          onSelectDay={(date) => setSelectedDay(date)}
          onEditAssignment={openEdit}
          onUnassignAssignment={setUnassignTarget}
        />
        <div className="mt-4 flex flex-col gap-2">
          <h2 className="font-display font-semibold text-foreground text-sm">
            {t('dispatch:rail.heading')}
          </h2>
          <ul aria-label={t('dispatch:rail.listLabel')} className="flex flex-col gap-2">
            {jobs.map((job) => (
              <li
                key={job.id}
                className="flex items-center justify-between rounded-md border border-border bg-card p-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground text-sm">{job.jobName}</p>
                  <p className="truncate text-muted-foreground text-xs">{job.status}</p>
                </div>
                <button
                  type="button"
                  onClick={() => openSchedule(job, null)}
                  className="inline-flex h-8 shrink-0 items-center rounded-md bg-secondary px-3 font-medium text-secondary-foreground text-xs transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('dispatch:rail.schedule')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <DispatchManageView categories={categories} colorPalette={colorPalette} />

      <ScheduleFallbackDialog
        open={scheduleJob !== null}
        jobLabel={scheduleJob?.jobName ?? ''}
        days={days}
        employees={employees}
        categories={categories}
        defaultDate={scheduleSlot?.date ?? effectiveDay}
        defaultStartTime={scheduleSlot?.startTime}
        error={writeError}
        pending={assignMutation.isPending}
        onConfirm={handleFallbackConfirm}
        onCancel={closeSchedule}
      />

      <AssignmentEditDialog
        open={editAssignment !== null}
        assignment={editAssignment}
        days={days}
        employees={employees}
        jobs={jobs}
        categories={categories}
        error={writeError}
        pending={editMutation.isPending}
        onConfirm={handleEditConfirm}
        onCancel={() => openEdit(null)}
      />

      <ConfirmDialog
        open={unassignTarget !== null}
        heading={t('dispatch:unassign.heading')}
        body={t('dispatch:unassign.body')}
        confirmLabel={t('dispatch:unassign.confirm')}
        cancelLabel={t('dispatch:fallback.cancel')}
        confirmPending={unassignMutation.isPending}
        onConfirm={handleConfirmUnassign}
        onCancel={() => setUnassignTarget(null)}
      />
    </section>
  );
}
